import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, UserPlus, Bank, FileText, X } from "@phosphor-icons/react";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useCompany } from "../../context/CompanyContext";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const DEPTS = ["Operations","Sales","Finance","Admin","HR","Other"];
const empty = {
  name:"", employee_code:"", designation:"", department:"Operations", location:"",
  base_salary:0, joining_date:"", email:"", phone:"", address:"",
  bank:{account_holder:"",account_number:"",ifsc:"",bank_name:"",branch:""},
  onboarding_status:"pending",
};

export default function Onboarding() {
  const { activeId } = useCompany();
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState("pending");

  const load = async () => {
    if (!activeId) { setEmployees([]); return; }
    const { data } = await api.get(`/finance/employees?company_id=${activeId}`);
    setEmployees(data);
  };
  useEffect(() => { load(); }, [activeId]);

  const openNew = () => { setEditing(null); setForm({...empty}); setOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({...empty, ...e, bank:e.bank||empty.bank}); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    if (!form.phone.trim()) { toast.error("Mobile is mandatory for onboarding"); return; }
    if (!form.email.trim()) { toast.error("Email is mandatory for onboarding"); return; }
    if (!activeId) { toast.error("Pick a company first"); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, company_id: activeId, base_salary: Number(form.base_salary)||0 };
      if (editing) await api.put(`/finance/employees/${editing.id}`, payload);
      else await api.post("/finance/employees", payload);
      toast.success(editing?"Updated":"Onboarded · pending review");
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSubmitting(false); }
  };

  const markCompleted = async (e) => {
    await api.put(`/finance/employees/${e.id}`, {...e, onboarding_status:"completed"});
    toast.success("Marked onboarded"); load();
  };

  const filtered = filter==="all" ? employees : employees.filter(e => (e.onboarding_status||"completed")===filter);

  return (
    <Layout title="Employee Onboarding" subtitle="HR · joining · documents · bank details"
      actions={<button onClick={openNew} data-testid="add-onboarding-button" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]"><UserPlus size={16} weight="bold"/> Onboard Employee</button>}>
      <div className="flex gap-2 mb-5">
        {["pending","completed","all"].map(t => (
          <button key={t} onClick={()=>setFilter(t)} data-testid={`filter-${t}`}
            className={`px-3 py-1.5 text-xs font-mono-shelv uppercase tracking-widest border ${filter===t?"bg-[#0A0A0A] text-white border-[#0A0A0A]":"border-black/15 hover:border-[#0A0A0A]"}`}>
            {t} ({t==="all" ? employees.length : employees.filter(e=>(e.onboarding_status||"completed")===t).length})
          </button>
        ))}
      </div>
      <div className="border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="onboarding-table">
          <thead className="bg-[#F5F6F8] text-left">
            <tr>{["Name","Designation","Mobile","Email","Bank","Status",""].map(h => <th key={h} className="px-4 py-3 font-mono-shelv text-[10px] uppercase tracking-widest text-[#717680]">{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-[#717680]">No employees{filter==="pending"?" pending onboarding":""}.</td></tr>}
            {filtered.map(e => (
              <tr key={e.id} className="border-t border-black/5 hover:bg-[#FAFBFC]" data-testid={`emp-row-${e.id}`}>
                <td className="px-4 py-3"><div className="font-semibold">{e.name}</div><div className="text-[10px] text-[#717680] font-mono-shelv">{e.employee_code||"—"}</div></td>
                <td className="px-4 py-3 text-[#4A4D57]">{e.designation || "—"}<div className="text-[10px] text-[#717680]">{e.department}</div></td>
                <td className="px-4 py-3 text-[#4A4D57]">{e.phone || "—"}</td>
                <td className="px-4 py-3 text-[#4A4D57]">{e.email || "—"}</td>
                <td className="px-4 py-3 text-[#4A4D57]">{e.bank?.account_number ? <span className="inline-flex items-center gap-1 text-xs text-[#D90429]"><Bank size={12} weight="bold"/> Linked</span> : <span className="text-[#9CA3AF] text-xs">—</span>}</td>
                <td className="px-4 py-3"><span className={`inline-block px-2 py-1 text-[10px] font-mono-shelv tracking-widest border ${e.onboarding_status==="pending"?"text-[#F4A261] border-[#F4A261]/40 bg-[#F4A261]/10":"text-[#0D9488] border-[#0D9488]/40 bg-[#0D9488]/10"}`}>{(e.onboarding_status||"completed").toUpperCase()}</span></td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {e.onboarding_status==="pending" && <button onClick={()=>markCompleted(e)} className="text-[11px] px-2 py-1 bg-[#0D9488] text-white mr-1" data-testid={`mark-done-${e.id}`}>Mark Onboarded</button>}
                  <button onClick={()=>openEdit(e)} className="text-[11px] px-2 py-1 border border-black/15" data-testid={`edit-emp-${e.id}`}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editing?"Edit employee":"Employee onboarding"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Name *</Label><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} className="rounded-none" data-testid="onb-name"/></div>
            <div><Label>Employee Code</Label><Input value={form.employee_code} onChange={e=>setForm({...form,employee_code:e.target.value})} className="rounded-none"/></div>
            <div><Label>Mobile *</Label><Input value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} className="rounded-none" data-testid="onb-phone"/></div>
            <div><Label>Email *</Label><Input value={form.email} onChange={e=>setForm({...form,email:e.target.value})} className="rounded-none" data-testid="onb-email"/></div>
            <div><Label>Designation</Label><Input value={form.designation} onChange={e=>setForm({...form,designation:e.target.value})} className="rounded-none"/></div>
            <div><Label>Department</Label>
              <Select value={form.department} onValueChange={v=>setForm({...form,department:v})}>
                <SelectTrigger className="rounded-none"><SelectValue/></SelectTrigger>
                <SelectContent>{DEPTS.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Location</Label><Input value={form.location} onChange={e=>setForm({...form,location:e.target.value})} className="rounded-none"/></div>
            <div><Label>Joining Date</Label><Input type="date" value={form.joining_date||""} onChange={e=>setForm({...form,joining_date:e.target.value})} className="rounded-none"/></div>
            <div><Label>Monthly Base Salary (₹)</Label><Input type="number" value={form.base_salary} onChange={e=>setForm({...form,base_salary:e.target.value})} className="rounded-none"/></div>
            <div className="col-span-2"><Label>Address</Label><Textarea value={form.address} onChange={e=>setForm({...form,address:e.target.value})} rows={2} className="rounded-none"/></div>
          </div>
          <div className="border-t border-black/10 pt-4 mt-4">
            <div className="overline mb-3 flex items-center gap-2"><Bank size={14} weight="bold"/> Bank details (for salary)</div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Account holder name" value={form.bank.account_holder} onChange={e=>setForm({...form,bank:{...form.bank,account_holder:e.target.value}})} className="rounded-none" data-testid="onb-bank-holder"/>
              <Input placeholder="Account number" value={form.bank.account_number} onChange={e=>setForm({...form,bank:{...form.bank,account_number:e.target.value}})} className="rounded-none"/>
              <Input placeholder="IFSC" value={form.bank.ifsc} onChange={e=>setForm({...form,bank:{...form.bank,ifsc:e.target.value}})} className="rounded-none"/>
              <Input placeholder="Bank name" value={form.bank.bank_name} onChange={e=>setForm({...form,bank:{...form.bank,bank_name:e.target.value}})} className="rounded-none"/>
              <Input placeholder="Branch" value={form.bank.branch} onChange={e=>setForm({...form,bank:{...form.bank,branch:e.target.value}})} className="rounded-none col-span-2"/>
            </div>
          </div>
          <DialogFooter>
            <button onClick={()=>setOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={save} disabled={submitting} data-testid="save-onb" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-50">{submitting?"Saving…":(editing?"Save":"Onboard")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
